import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import apiClient, { setTokenGetter, setUnauthorizedHandler } from '../clients/apiClient';
import { cleanOAuthParams, parseOAuthParams } from '../utils/auth';
import type { User } from '../data/mock';

interface AuthState {
  user: User | null;
  access_token: string | null;
  refresh_token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface AuthContextValue extends AuthState {
  login: () => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    access_token: null,
    refresh_token: null,
    isLoading: true,
    isAuthenticated: false,
  });

  // Ref holds the token synchronously — no wait for React state flush
  const tokenRef = useRef<string | null>(null);

  // Keep a ref so the unauthorized handler always sees the latest logout
  const logoutRef = useRef<(() => Promise<void>) | undefined>(undefined);

  // Point the interceptor at the ref once, on mount
  useEffect(() => {
    setTokenGetter(() => tokenRef.current);
    setUnauthorizedHandler(() => logoutRef.current?.());
  }, []);

  const storeToken = useCallback((token: string, refreshToken?: string) => {
    tokenRef.current = token;
    localStorage.setItem('access_token', token);
    if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
    setState((s) => ({ ...s, access_token: token, ...(refreshToken ? { refresh_token: refreshToken } : {}) }));
  }, []);

  const clearAuth = useCallback(() => {
    tokenRef.current = null;
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setState({ user: null, access_token: null, refresh_token: null, isLoading: false, isAuthenticated: false });
  }, []);

  const normalizeUser = (data: User): User => ({ ...data, name: data.full_name ?? data.name ?? '' });

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await apiClient.get<User>('/api/auth/me');
      setState((s) => ({ ...s, user: normalizeUser(data), isAuthenticated: true, isLoading: false }));
    } catch {
      clearAuth();
    }
  }, [clearAuth]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await apiClient.get<User>('/api/auth/me');
      setState((s) => ({ ...s, user: normalizeUser(data) }));
    } catch {
      console.error('Failed to refresh user');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // On mount: check for OAuth callback params
  useEffect(() => {
    const oauthParams = parseOAuthParams();
    console.log('OAuth params:', oauthParams
      ? { user_id: oauthParams.user_id, gmail_connected: oauthParams.gmail_connected, access_token: 'present', refresh_token: 'present' }
      : null);

    if (oauthParams) {
      cleanOAuthParams();
      storeToken(oauthParams.access_token, oauthParams.refresh_token);
      console.log('Token stored, fetching user...');
      fetchMe();
      return;
    }

    // Check localStorage for a persisted session
    const savedToken = localStorage.getItem('access_token');
    const savedRefresh = localStorage.getItem('refresh_token');
    if (savedToken) {
      tokenRef.current = savedToken;
      setState((s) => ({ ...s, access_token: savedToken, refresh_token: savedRefresh }));
      fetchMe();
      return;
    }

    // No session found — not authenticated
    setState((s) => ({ ...s, isLoading: false }));
  }, [fetchMe, storeToken]);

  const login = useCallback(() => {
    window.location.href = `${process.env.REACT_APP_API_URL ?? 'http://localhost:8000'}/api/auth/google`;
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiClient.post('/api/auth/logout');
    } catch {
      // ignore — clear state regardless
    }
    clearAuth();
    window.location.href = '/';
  }, [clearAuth]);

  logoutRef.current = logout;

  return (
    <AuthContext.Provider value={{ ...state, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthContext must be used inside AuthProvider');
  return ctx;
}
